import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, type SubmitHandler } from "react-hook-form";
import { API_URL } from "../../services/api";
import InputField from "../../components/ui/InputField";
import Button from "../../components/ui/Button";

import Logo from "../../components/ui/Logo";

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [step] = useState<"email" | "reset">(searchParams.get("token") ? "reset" : "email"
  );
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const password = watch("password");

  // Handle email submission for password reset
  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) {
      return;
    }

    setIsLoading(true);
    try {


      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send reset email');
      }

      setEmailSent(true);
    } catch (error) {
      console.error("Error:", error);
      // For demo purposes, simulate success
      setEmailSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset
  const onSubmit: SubmitHandler<ResetPasswordFormData> = async (data) => {
    if (data.password !== data.confirmPassword) {
      setError("confirmPassword", {
        message: "Passwords do not match",
      });
      return;
    }

    setIsLoading(true);
    try {

      
      const token = searchParams.get("token");
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: data.password
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to reset password');
      }

      navigate("/login", {
        state: { message: "Password reset successfully! Please login with your new password." }
      });
    } catch (error) {
      console.error("Reset password error:", error);
      setError("root", {
        message: error instanceof Error ? error.message : "Failed to reset password. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-linear-to-br from-pink-200 to-indigo-100 px-4 py-8">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-lg shadow-lg p-4">
          {/* Top Header */}
          <div className="mb-8 px-5 py-3">
            <Logo />
            <h1 className="text-2xl font-bold text-gray-900 mt-5 tracking-tight">
              {step === "email" ? "Forgot Password?" : "Reset Password"}
            </h1>
            <p className="text-gray-500 mt-1.5 text-sm">
              {step === "email"
                ? "No worries, we'll send you reset instructions"
                : "Enter your new password below"
              }
            </p>
          </div>

          {step === "email" ? (
            <form onSubmit={handleEmailSubmit} className="space-y-4 px-5">
              {!emailSent ? (
                <div className="border border-gray-300 rounded p-4 bg-slate-100 space-y-4">
                  <div>
                    <h2 className="font-medium">Reset Password</h2>
                    <p className="text-sm text-gray-700">Enter your email address and we&apos;ll send you a link to reset your password.</p>
                  </div>
                  <InputField
                    type="email"
                    placeholder="Enter your email"
                    variant="outline"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                    className="focus:border-purple-600"
                  />
                  <Button
                    type="submit"
                    disabled={isLoading || !email.trim()}
                    className="w-full"
                  >
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </Button>

                  <p className="text-[10px] text-center text-gray-600">Security tip: For your protection, the reset link will expire in 1 hour.</p>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Check your email</h3>
                  <p className="text-gray-600">
                    We sent a password reset link to {email}
                  </p>
                  {/* <Button
                    onClick={() => navigate("/login")}
                    variant="outline"
                    className="w-full"
                  >
                    Back to Login
                  </Button> */}
                </div>
              )}
            </form>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-5">
              <InputField
                type="password"
                placeholder="New password"
                variant="outline"
                disabled={isLoading}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
                error={errors.password?.message}
                className="focus:border-purple-600"
              />
              <InputField
                type="password"
                placeholder="Confirm new password"
                variant="outline"
                disabled={isLoading}
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
                error={errors.confirmPassword?.message}
                className="focus:border-purple-600"
              />

              {errors.root && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-600 text-sm">{errors.root.message}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          )}

          <div className="mt-6 px-5">
            <Button
              onClick={() => navigate("/login")}
              variant="borderless"
              className="w-full text-purple-600"
            >
              ← Back to Login
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}